import React, { useMemo, useRef, useState } from 'react';
import { StageScreen, StagePopup, popupHasContent } from './components/StageShell';
import { s3PublicUrl, s3PublicBaseUrl } from '../../utils/s3PublicUrl';
import {
  normalizeStage7,
  STAGE7_MAX_TECH_PICK,
} from '../../admin/pages/futureAdmin/futureAdminNormalize';

function assetUrl(filename) {
  const s = String(filename ?? '').trim();
  return s ? s3PublicUrl(s) : '';
}

const TECH_DESK_SCREENS = 0;
const TECH_ROBOT = 1;
const TECH_HUMAN = 2;
const TECH_VR = 3;

const DESK_POSITIONS = [
  { left: '20%', top: '78%' },
  { left: '40%', top: '78%' },
  { left: '60%', top: '78%' },
  { left: '80%', top: '78%' },
  { left: '16%', top: '89%', width: '11%' },
  { left: '38%', top: '89%', width: '11%' },
  { left: '62%', top: '89%', width: '11%' },
  { left: '84%', top: '89%', width: '11%' },
];

const CHILD_POSITIONS = [
  { left: '30%', bottom: '7%' },
  { left: '67%', bottom: '7%' },
];

const STUDENT_POSITION = { left: '50%', bottom: '12%' };

const TEACHER_POSITIONS = {
  human: { left: '69%', bottom: '24%' },
  robot: { left: '77%', bottom: '23%' },
};

// Swap S3 img srcs to backend proxy srcs so html2canvas can capture
// without CORS errors or canvas taint.
function swapToProxySrcs(el, backendUrl) {
  const s3Base = s3PublicBaseUrl();
  const swaps = [];
  el.querySelectorAll('img[src]').forEach((img) => {
    const src = img.getAttribute('src');
    if (src && s3Base && src.startsWith(s3Base)) {
      const key = src.slice(s3Base.length);
      swaps.push({ img, orig: src });
      img.setAttribute('src', `${backendUrl}/themeAsset?key=${encodeURIComponent(key)}`);
    }
  });
  return swaps;
}

function restoreOriginalSrcs(swaps) {
  swaps.forEach(({ img, orig }) => img.setAttribute('src', orig));
}

async function waitForImages(el) {
  const imgs = Array.from(el.querySelectorAll('img'));
  await Promise.all(imgs.map((img) =>
    img.complete
      ? Promise.resolve()
      : new Promise((res) => { img.onload = res; img.onerror = res; })
  ));
}

async function loadHtml2Canvas() {
  if (window.html2canvas) return window.html2canvas;
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
    s.onload = () => resolve(window.html2canvas);
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

function ChildFigure({ image, pos }) {
  if (!image) return null;
  return (
    <div className="fi-school-child" style={{ left: pos.left, bottom: pos.bottom }}>
      <div className="fi-school-child__figure">
        <img className="fi-school-child__body" src={image} alt="" />
      </div>
    </div>
  );
}

function OptionThumb({ item, selected, onSelect, disabled }) {
  const src = assetUrl(item.image);
  return (
    <button
      type="button"
      className={`fi-school-option${selected ? ' fi-school-option--selected' : ''}`}
      onClick={onSelect}
      disabled={disabled && !selected}
      title={item.label}
    >
      {src ? <img src={src} alt={item.label} /> : <span className="fi-school-option__placeholder" />}
      <span>{item.label}</span>
    </button>
  );
}

export default function Stage7School({ stage, pagesBg, onComplete, adminToken, userId }) {
  const data = useMemo(() => normalizeStage7(stage), [stage]);
  const startHasContent = popupHasContent(stage?.start_popup_image, stage?.start_popup_text);
  const endHasContent = popupHasContent(stage?.end_popup_image, stage?.end_popup_text);
  const [phase, setPhase] = useState(startHasContent ? 'start' : 'game');
  const [bgIndex, setBgIndex] = useState(null);
  const [boardIndex, setBoardIndex] = useState(null);
  const [techIndices, setTechIndices] = useState([]);
  const [uploading, setUploading] = useState(false);

  const schoolImageRef = useRef(null);
  const previewRef = useRef(null);

  const toggleTech = (index) => {
    setTechIndices((prev) => {
      if (prev.includes(index)) return prev.filter((i) => i !== index);
      const isTeacherSlot = index === TECH_ROBOT || index === TECH_HUMAN;
      const other = index === TECH_ROBOT ? TECH_HUMAN : TECH_ROBOT;
      let next = isTeacherSlot ? prev.filter((i) => i !== other) : prev;
      if (next.length >= STAGE7_MAX_TECH_PICK) return prev;
      return [...next, index];
    });
  };

  const techDisabledAt = (index) => {
    if (techIndices.length < STAGE7_MAX_TECH_PICK) return false;
    const isTeacherSlot = index === TECH_ROBOT || index === TECH_HUMAN;
    const other = index === TECH_ROBOT ? TECH_HUMAN : TECH_ROBOT;
    if (isTeacherSlot && techIndices.includes(other)) return false;
    return true;
  };

  const canProceed =
    bgIndex != null &&
    boardIndex != null &&
    techIndices.length === STAGE7_MAX_TECH_PICK;

  const selectedBg = bgIndex != null ? data.backgrounds[bgIndex] : null;
  const selectedBoard = boardIndex != null ? data.class_boards[boardIndex] : null;
  const selectedTechs = techIndices.map((i) => data.technologies[i]).filter(Boolean);

  const deskScreensTech = techIndices.includes(TECH_DESK_SCREENS) ? data.technologies[TECH_DESK_SCREENS] : null;
  const robotTech = techIndices.includes(TECH_ROBOT) ? data.technologies[TECH_ROBOT] : null;
  const humanTech = techIndices.includes(TECH_HUMAN) ? data.technologies[TECH_HUMAN] : null;
  const vrSelected = techIndices.includes(TECH_VR);
  const vrImage = assetUrl(data.technologies[TECH_VR]?.image);
  const deskScreenSrc = assetUrl(deskScreensTech?.image);
  const teacherTech = robotTech || humanTech;
  const teacherSrc = assetUrl(teacherTech?.image);
  const teacherPos = robotTech ? TEACHER_POSITIONS.robot : TEACHER_POSITIONS.human;

  const finish = () => {
    onComplete({
      background: selectedBg?.label || '',
      background_image: selectedBg?.image || '',
      class_board: selectedBoard?.label || '',
      board_image: selectedBoard?.image || '',
      technologies: selectedTechs.map((t) => t.label),
      school_image: schoolImageRef.current || '',
    });
  };

  const handleNext = async () => {
    if (!canProceed || uploading) return;
    setUploading(true);

    // Capture the preview div and upload to S3
    if (previewRef.current && adminToken) {
      let swaps = [];
      try {
        const backendUrl = String(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
        // Replace S3 image srcs with backend proxy so canvas isn't tainted
        swaps = swapToProxySrcs(previewRef.current, backendUrl);
        await waitForImages(previewRef.current);

        const h2c = await loadHtml2Canvas();
        const canvas = await h2c(previewRef.current, {
          scale: 2,
          useCORS: true,
          backgroundColor: null,
        });

        restoreOriginalSrcs(swaps);
        swaps = [];

        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
        if (blob) {
          const fd = new FormData();
          fd.append('school_image', blob, 'school-preview.png');
          fd.append('userId', String(userId || ''));
          const resp = await fetch(`${backendUrl}/uploadSchoolImage`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${adminToken}` },
            body: fd,
          });
          const result = await resp.json();
          if (result.success) schoolImageRef.current = result.filename;
        }
      } catch (err) {
        restoreOriginalSrcs(swaps);
        console.warn('School preview capture failed:', err);
      }
    }

    setUploading(false);
    if (endHasContent) setPhase('end');
    else finish();
  };

  if (phase === 'start') {
    return (
      <StageScreen pagesBg={pagesBg} pageKey="stage_7" stage={stage}>
        <StagePopup
          image={stage?.start_popup_image}
          text={stage?.start_popup_text}
          onNext={() => setPhase('game')}
        />
      </StageScreen>
    );
  }

  if (phase === 'end') {
    return (
      <StageScreen pagesBg={pagesBg} pageKey="stage_7" stage={stage}>
        <StagePopup
          image={stage?.end_popup_image}
          text={stage?.end_popup_text}
          onNext={finish}
        />
      </StageScreen>
    );
  }

  return (
    <StageScreen pagesBg={pagesBg} pageKey="stage_7" stage={stage}>
      <div className="fi-stage-root fi-school-root">
      <h2 className="fi-school-title">{data.title}</h2>
      <div className="fi-school-builder">
        <aside className="fi-school-panel">
          <section className="fi-school-section">
            <h3>Select any background (Any 1)</h3>
            <div className="fi-school-options">
              {data.backgrounds.map((item, i) => (
                <OptionThumb
                  key={`bg-${i}`}
                  item={item}
                  selected={bgIndex === i}
                  onSelect={() => setBgIndex(i)}
                />
              ))}
            </div>
          </section>
          <section className="fi-school-section">
            <h3>Select Class Board (Any 1)</h3>
            <div className="fi-school-options">
              {data.class_boards.map((item, i) => (
                <OptionThumb
                  key={`board-${i}`}
                  item={item}
                  selected={boardIndex === i}
                  onSelect={() => setBoardIndex(i)}
                />
              ))}
            </div>
          </section>
          <section className="fi-school-section">
            <h3>Select Technology (Any {STAGE7_MAX_TECH_PICK})</h3>
            <div className="fi-school-options">
              {data.technologies.map((item, i) => (
                <OptionThumb
                  key={`tech-${i}`}
                  item={item}
                  selected={techIndices.includes(i)}
                  onSelect={() => toggleTech(i)}
                  disabled={techDisabledAt(i)}
                />
              ))}
            </div>
            <p className="fi-school-tech-count">
              {techIndices.length} / {STAGE7_MAX_TECH_PICK} selected
            </p>
          </section>
          <div className="fi-actions">
            <button
              type="button"
              className="fi-btn fi-btn--primary"
              disabled={!canProceed || uploading}
              onClick={handleNext}
            >
              {uploading ? 'Saving…' : 'Next'}
            </button>
          </div>
        </aside>
        <div className="fi-school-preview" ref={previewRef} aria-label="School preview">
          {selectedBg?.image ? (
            <img
              className="fi-school-preview__layer fi-school-preview__bg"
              src={assetUrl(selectedBg.image)}
              alt=""
            />
          ) : (
            <div className="fi-school-preview__empty">Select a background</div>
          )}
          {selectedBoard?.image ? (
            <img
              className="fi-school-preview__board"
              src={assetUrl(selectedBoard.image)}
              alt=""
            />
          ) : null}
          {teacherSrc ? (
            <img
              className="fi-school-teacher"
              style={{ left: teacherPos.left, bottom: teacherPos.bottom }}
              src={teacherSrc}
              alt={teacherTech.label}
            />
          ) : null}
          {deskScreenSrc ? (
            <div className="fi-school-desks" aria-hidden="true">
              {DESK_POSITIONS.map((pos, i) => (
                <img
                  key={`desk-screen-${i}`}
                  className="fi-school-desk-screen"
                  style={{ left: pos.left, top: pos.top, ...(pos.width ? { width: pos.width } : {}) }}
                  src={deskScreenSrc}
                  alt=""
                />
              ))}
            </div>
          ) : null}
          <div className="fi-school-children" aria-hidden="true">
            <ChildFigure image={assetUrl(data.child_1_image)} pos={CHILD_POSITIONS[0]} />
            <ChildFigure image={assetUrl(data.child_2_image)} pos={CHILD_POSITIONS[1]} />
            <ChildFigure image={assetUrl(data.student_image)} pos={STUDENT_POSITION} />
          </div>
          {vrSelected && vrImage ? (
            <div className="fi-school-vr-overlays" aria-hidden="true">
              <img className="fi-school-vr-overlay" style={{ left: CHILD_POSITIONS[0].left, bottom: '30%' }} src={vrImage} alt="" />
              <img className="fi-school-vr-overlay fi-school-vr-overlay--flip" style={{ left: '60%', bottom: '31%' }} src={vrImage} alt="" />
            </div>
          ) : null}
        </div>
      </div>
      </div>
    </StageScreen>
  );
}
